import React from 'react';
import PropTypes from 'prop-types';
import { Typography, Tooltip } from '@material-ui/core';

import { STONLY_ID } from 'src/lib/stonly';
import { formatNumber } from 'src/utils/format';
import { contentTypeFromBriefType } from 'src/data/brief';
import { getContentTypeTaxonomyName, getContentTypeTaxonomyHint } from 'src/data/contentType';
import Score, { getScoreProps, SCORE_SIZE } from 'src/components/Score';
import { ButtonText } from 'src/components/ButtonText';
import { StonlyIcon } from 'src/components/StonlyIcon';
import { IconStack } from 'src/assets/icons/IconStack';
import { Loader } from './components/Loader';
import { IconInfoContained } from 'src/assets/icons/IconInfoContained';
import { useStyles } from './useStyles';

export const ScoringMethod = ({
  briefType,
  score,
  basedOnValue,
  loading,
  blocked,
  locked,
  readOnly,
  displayScoringMethod,
  scoringMethodName,
  onScoringMethodClick,
}) => {
  const styles = useStyles();
  const { title } = getScoreProps(score, locked);

  const contentType = contentTypeFromBriefType(briefType);
  const contentTypeTaxonomyName = getContentTypeTaxonomyName(contentType);
  const displayedName = scoringMethodName || `Undefined ${contentTypeTaxonomyName}`;
  const similarCopiesFormatted = formatNumber(blocked ? 0 : basedOnValue || 0);
  const showModel = displayScoringMethod && !loading;

  if (loading) {
    return <Loader />;
  }

  return (
    <div className={styles.root}>
      <div className={styles.scoreContainer}>
        <Score
          classes={{ score: styles.score }}
          score={score}
          size={SCORE_SIZE.xxxl}
          locked={locked}
          strokeWidth={4}
        />
      </div>

      <div className={styles.texts}>
        <div className="flex">
          <Typography className={styles.title}>{title}</Typography>

          <StonlyIcon
            className={styles.stonlyHintIcon}
            stonlyId={STONLY_ID.predictiveScore}
          />
        </div>

        {showModel && onScoringMethodClick && (
          <div className={styles.subtitleContainer}>
            <IconStack className={styles.subtitleIcon} />
            <Typography className={styles.subtitle}>
              {`Scoring model: ${displayedName}.`}
              <Tooltip title={getContentTypeTaxonomyHint(contentType)}>
                <ButtonText
                  className={styles.changeModelButton}
                  onClick={!readOnly && !locked ? onScoringMethodClick : undefined}
                >
                  Change
                </ButtonText>
              </Tooltip>
            </Typography>
          </div>
        )}

        <div className={styles.subtitleContainer}>
          <IconInfoContained className={styles.subtitleIcon} />
          <Typography
            className={styles.subtitle}
          >{`Compared to ${similarCopiesFormatted} similar copy variations`}</Typography>
        </div>
      </div>
    </div>
  );
};

ScoringMethod.propTypes = {
  briefType: PropTypes.string,
  score: PropTypes.number,
  basedOnValue: PropTypes.number,
  loading: PropTypes.bool,
  blocked: PropTypes.bool,
  locked: PropTypes.bool,
  readOnly: PropTypes.bool,
  displayScoringMethod: PropTypes.bool,
  scoringMethodName: PropTypes.string,
  onScoringMethodClick: PropTypes.func,
};

ScoringMethod.defaultValues = {
  briefType: null,
  score: null,
  basedOnValue: null,
  loading: null,
  blocked: null,
  locked: null,
  readOnly: null,
  displayScoringMethod: null,
  scoringMethodName: null,
  onScoringMethodClick: null,
};
